import { ServiceLayout } from "@/components/service/ServiceLayout";
import { ServiceFAQ } from "@/components/service/ServiceFAQ";
import { Button } from "@/components/ui/button";
import { Mail, Target, BarChart3, Users, Zap, Award } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ServiceCTA } from "@/components/ui/ServiceCTA";
import { serviceFAQs } from "@/data/service-faqs";

const EmailMarketing = () => {
  return (
    <ServiceLayout serviceName="Email Marketing" className="bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
              Transform Your Email Marketing Strategy
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Drive engagement, boost conversions, and build lasting relationships with your customers through data-driven email marketing campaigns. By leveraging AI-powered automation and smart personalization, we optimize your email workflows to deliver the right message at the right time, ensuring maximum impact and efficiency.
            </p>
            <Link to="/demo">
              <Button size="lg" className="mr-4">
                Get Your Free Analysis
              </Button>
            </Link>
            <Link to="/demo">
              <Button variant="outline" size="lg">
                Schedule a Demo
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-6 rounded-lg bg-gradient-to-br from-blue-50 to-purple-50">
              <h3 className="text-4xl font-bold text-blue-600 mb-2">45%</h3>
              <p className="text-gray-600">Average Open Rate</p>
            </div>
            <div className="p-6 rounded-lg bg-gradient-to-br from-blue-50 to-purple-50">
              <h3 className="text-4xl font-bold text-blue-600 mb-2">3.2x</h3>
              <p className="text-gray-600">ROI Improvement</p>
            </div>
            <div className="p-6 rounded-lg bg-gradient-to-br from-blue-50 to-purple-50">
              <h3 className="text-4xl font-bold text-blue-600 mb-2">1M+</h3>
              <p className="text-gray-600">Emails Delivered Monthly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Our Email Marketing Services</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <ServiceCard
              icon={<Target className="w-8 h-8 text-blue-600" />}
              title="Campaign Strategy"
              description="Data-driven email campaign planning tailored to your audience and business goals."
            />
            <ServiceCard
              icon={<Mail className="w-8 h-8 text-blue-600" />}
              title="Email Design & Content"
              description="Eye-catching, responsive email templates with compelling content that converts."
            />
            <ServiceCard
              icon={<BarChart3 className="w-8 h-8 text-blue-600" />}
              title="Analytics & Optimization"
              description="Continuous performance monitoring and optimization for maximum ROI."
            />
            <ServiceCard
              icon={<Users className="w-8 h-8 text-blue-600" />}
              title="List Management"
              description="Strategic segmentation and list hygiene for better targeting and deliverability."
            />
            <ServiceCard
              icon={<Zap className="w-8 h-8 text-blue-600" />}
              title="Automation"
              description="Sophisticated email automation workflows that nurture leads and drive sales through AI agents."
            />
            <ServiceCard
              icon={<Award className="w-8 h-8 text-blue-600" />}
              title="A/B Testing"
              description="Systematic testing of subject lines, content, and CTAs for optimal performance."
            />
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Our Process</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <ProcessStep
              number="01"
              title="Discovery"
              description="Understanding your business goals and target audience"
            />
            <ProcessStep
              number="02"
              title="Strategy"
              description="Developing a customized email marketing plan"
            />
            <ProcessStep
              number="03"
              title="Execution"
              description="Creating and launching targeted campaigns"
            />
            <ProcessStep
              number="04"
              title="Optimization"
              description="Analyzing results and refining strategies"
            />
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={serviceFAQs["email-marketing"]} serviceName="Email Marketing" />
      <ServiceCTA serviceName="Email Marketing" />
    </ServiceLayout>
  );
};

const ServiceCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => {
  return (
    <div className="p-6 border rounded-lg hover:shadow-lg transition-all duration-300 bg-white">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-semibold mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const ProcessStep = ({ number, title, description }: { number: string; title: string; description: string }) => {
  return (
    <div className="text-center">
      <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-bold mx-auto mb-4">
        {number}
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

export default EmailMarketing;
